import { toast } from "sonner";
import { Trash2 } from "lucide-react";

// Generic paste-enabled editable grid used for bulk add/edit flows.
// cols: [{ key, label, w, type?, options?, transform?(val) }]
// rows/setRows: array state of row objects (extra keys like `id` are preserved).
export function BulkGrid({ cols, rows, setRows }) {
  const emptyRow = () => cols.reduce((o, c) => ({ ...o, [c.key]: "" }), {});
  const setCell = (ri, key, v) => setRows(rows.map((r, i) => (i === ri ? { ...r, [key]: v } : r)));
  const removeRow = (ri) => setRows(rows.filter((_, i) => i !== ri));

  const onPaste = (e, ri, ci) => {
    const text = e.clipboardData.getData("text");
    if (!text || (!text.includes("\t") && !text.includes("\n"))) return; // single value: default paste
    e.preventDefault();
    const grid = text
      .replace(/\r/g, "")
      .split("\n")
      .filter((l, i, arr) => !(i === arr.length - 1 && l === ""))
      .map((l) => l.split("\t"));
    const next = rows.map((r) => ({ ...r }));
    grid.forEach((line, r) => {
      const idx = ri + r;
      while (next.length <= idx) next.push(emptyRow());
      line.forEach((val, c) => {
        const col = cols[ci + c];
        if (!col) return;
        next[idx][col.key] = col.transform ? col.transform(val) : val.trim();
      });
    });
    setRows(next);
    toast.success(`Pasted ${grid.length} row${grid.length > 1 ? "s" : ""}`);
  };

  return (
    <div className="max-h-[55vh] overflow-auto border border-border">
      <table className="w-full border-collapse text-sm">
        <thead className="sticky top-0 z-10">
          <tr>
            <th className="w-8 border border-border bg-secondary px-2 py-2" />
            {cols.map((c) => (
              <th key={c.key} className={`border border-border bg-secondary px-3 py-2 text-left text-xs font-semibold ${c.w || ""}`}>{c.label}</th>
            ))}
            <th className="w-10 border border-border bg-secondary px-2 py-2" />
          </tr>
        </thead>
        <tbody>
          {rows.map((row, ri) => (
            <tr key={ri} data-testid={`bulk-row-${ri}`}>
              <td className="border border-border bg-secondary/50 px-2 py-1 text-center text-xs text-muted-foreground">{ri + 1}</td>
              {cols.map((c, ci) => (
                <td key={c.key} className="border border-border p-0">
                  <input
                    type={c.type === "date" ? "date" : "text"}
                    list={c.options ? `dl-${c.key}` : undefined}
                    value={row[c.key] ?? ""}
                    onChange={(e) => setCell(ri, c.key, e.target.value)}
                    onPaste={(e) => onPaste(e, ri, ci)}
                    onBlur={c.transform ? (e) => setCell(ri, c.key, c.transform(e.target.value)) : undefined}
                    className="w-full bg-transparent px-2 py-1.5 text-xs outline-none focus:bg-accent/40"
                    data-testid={`bulk-${c.key}-${ri}`}
                  />
                </td>
              ))}
              <td className="border border-border p-0 text-center">
                <button onClick={() => removeRow(ri)} className="px-2 py-1 text-muted-foreground hover:text-destructive" data-testid={`bulk-remove-${ri}`}>
                  <Trash2 className="h-3.5 w-3.5" />
                </button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      {cols.filter((c) => c.options).map((c) => (
        <datalist key={c.key} id={`dl-${c.key}`}>{c.options.map((o) => (<option key={o} value={o} />))}</datalist>
      ))}
    </div>
  );
}
